// The files of messages the server hasn't confirmed yet, held by the message's
// clientMessageId so a sending or failed row can show them and Retry can
// upload them again. Held until the server confirms the message or the
// reader discards it.
//
// What a held file needs in order to be shown is the platform's: the browser
// mints an object URL for a Blob's preview and must revoke it again, while a
// file on the phone is its own preview. Those two steps are the options; the
// holder itself is the same everywhere.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { IPendingAttachment } from '../../interfaces/ICommsApi.js';

export interface ILocalAttachments {
  byClientMessageId: Record<string, IPendingAttachment[]>;
  hold: (clientMessageId: string, files: IPendingAttachment[]) => void;
  release: (clientMessageId: string) => void;
  /** The held files as of now, for callbacks that must not re-create on every change. */
  read: (clientMessageId: string) => IPendingAttachment[];
}

export interface IUseLocalAttachmentsOptions {
  /** Prepare a file for showing while held (the web mints its preview URL here). */
  preview?: (file: IPendingAttachment) => IPendingAttachment;
  /** Let go of what `preview` made, on release and on unmount. */
  free?: (file: IPendingAttachment) => void;
}

export function useLocalAttachments(
  options: IUseLocalAttachmentsOptions = {},
): ILocalAttachments {
  const { preview, free } = options;
  const [byClientMessageId, setByClientMessageId] = useState<Record<string, IPendingAttachment[]>>({});
  const heldRef = useRef<Record<string, IPendingAttachment[]>>({});
  const freeRef = useRef(free);
  useEffect(() => {
    freeRef.current = free;
  }, [free]);

  const hold = useCallback((clientMessageId: string, files: IPendingAttachment[]) => {
    const held = preview ? files.map(preview) : files;
    heldRef.current = { ...heldRef.current, [clientMessageId]: held };
    setByClientMessageId(heldRef.current);
  }, [preview]);

  const release = useCallback((clientMessageId: string) => {
    if (!(clientMessageId in heldRef.current)) return;
    heldRef.current[clientMessageId]?.forEach((file) => freeRef.current?.(file));
    const { [clientMessageId]: _released, ...rest } = heldRef.current;
    heldRef.current = rest;
    setByClientMessageId(rest);
  }, []);

  const read = useCallback((clientMessageId: string) => heldRef.current[clientMessageId] ?? [], []);

  // Free whatever is still held when the page goes away.
  useEffect(
    () => () => {
      for (const files of Object.values(heldRef.current)) files.forEach((file) => freeRef.current?.(file));
    },
    [],
  );

  return { byClientMessageId, hold, release, read };
}
